import { useCallback, useEffect, useMemo, useRef, useState } from "react";

type Request = {
  change_id: string;
  asset_urn: string;
  source_column: string;
  target_column: string;
  repository_model: string;
  reason: string;
};

type Consumer = {
  urn: string;
  name: string;
  entity_type: string;
  platform: string | null;
  impacted_columns: string[];
};

type Artifact = {
  relative_path: string;
  sha256: string;
  bytes_written: number;
};

type Proof = {
  status: "BLOCKED" | "PLANNED" | "COMPLETED";
  context_digest: string;
  packet_digest: string;
  request: Request;
  datahub_context: {
    asset_name: string;
    asset_urn: string;
    platform: string | null;
    owners: Array<{ urn: string; owner_type: string | null }>;
    domain: string | null;
    tags: string[];
    downstream: Consumer[];
    query_examples: Array<{ source: string; statement: string }>;
  };
  decision: {
    risk: string;
    score: number;
    reasons: string[];
    approval_required: boolean;
  };
  approval: null | {
    approved_by: string;
    approved_at: string;
    approved_context_digest: string;
    approved_packet_digest: string;
  };
  artifacts: Artifact[];
  verification: null | Record<string, boolean | number | string>;
  datahub_writeback: {
    document_urn: string | null;
    fresh_read_verified: boolean;
    operations: string[];
  };
};

type RunEvent = {
  sequence: number;
  at: string;
  stage: string;
  operation: string;
  detail: string;
  data: Record<string, unknown>;
};

type Run = {
  run_id: string;
  mode: "fixture" | "live";
  state: "IDLE" | "RUNNING" | "PLANNED" | "BLOCKED" | "COMPLETED" | "ERROR";
  request: Request;
  events: RunEvent[];
  proof: Proof | null;
  error: string | null;
};

type Config = {
  mode: "fixture" | "live";
  connection_label: string;
  default_request: Request;
};

const stages = ["CHANGE", "CONTEXT", "PLAN", "APPROVAL", "VERIFY", "PROOF"];
const baseUrl = import.meta.env.BASE_URL;
const capturedOnly = import.meta.env.VITE_CAPTURED_MODE === "true";

function shortDigest(value?: string | null) {
  if (!value) return "Awaiting context";
  return `${value.slice(7, 15)}…${value.slice(-4)}`;
}

function ownerName(urn?: string | null) {
  if (!urn) return "Awaiting DataHub owner";
  return urn.split(":").at(-1)?.replaceAll("-", " ") ?? urn;
}

function humanOperation(value: string) {
  return value.replaceAll("_", " ");
}

function timeLabel(value: string) {
  return new Date(value).toLocaleTimeString("en-GB", {
    hour12: false,
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    fractionalSecondDigits: 3,
    timeZone: "UTC",
  });
}

async function jsonRequest<T>(url: string, options?: RequestInit): Promise<T> {
  const response = await fetch(url, options);
  if (!response.ok) {
    const body = await response.text();
    throw new Error(body || `${response.status} ${response.statusText}`);
  }
  return response.json() as Promise<T>;
}

export function App() {
  const [config, setConfig] = useState<Config | null>(null);
  const [run, setRun] = useState<Run | null>(null);
  const [capturedProof, setCapturedProof] = useState<Proof | null>(null);
  const [selectedFile, setSelectedFile] = useState<string | null>(null);
  const [fileContent, setFileContent] = useState("");
  const [error, setError] = useState<string | null>(null);
  const pollRef = useRef<number | null>(null);

  const proof = run?.proof ?? capturedProof;
  const request = run?.request ?? config?.default_request ?? capturedProof?.request;
  const isApi = config !== null;
  const isBusy = run?.state === "RUNNING" || run?.state === "IDLE";
  const isApproval = run?.state === "PLANNED";
  const canStart = isApi && (!run || ["COMPLETED", "BLOCKED", "ERROR"].includes(run.state));
  const connectionLabel = config?.connection_label ?? "Captured fixture proof";

  useEffect(() => {
    const loadCapturedProof = () =>
      fetch(`${baseUrl}proof.json`)
        .then((response) => {
          if (!response.ok) throw new Error("Captured proof is unavailable");
          return response.json() as Promise<Proof>;
        })
        .then(setCapturedProof)
        .catch((reason: Error) => setError(reason.message));

    if (capturedOnly) {
      void loadCapturedProof();
      return;
    }

    jsonRequest<Config>("/api/config")
      .then(setConfig)
      .catch(loadCapturedProof);
  }, []);

  const refresh = useCallback(async (runId: string) => {
    const next = await jsonRequest<Run>(`/api/runs/${runId}`);
    setRun(next);
    if (next.state === "RUNNING" || next.state === "IDLE") {
      pollRef.current = window.setTimeout(() => void refresh(runId), 250);
    }
  }, []);

  useEffect(() => () => {
    if (pollRef.current !== null) window.clearTimeout(pollRef.current);
  }, []);

  useEffect(() => {
    const first = proof?.artifacts[0]?.relative_path;
    if (first && !proof?.artifacts.some((item) => item.relative_path === selectedFile)) {
      setSelectedFile(first);
    }
  }, [proof, selectedFile]);

  useEffect(() => {
    if (!selectedFile) {
      setFileContent("");
      return;
    }
    const url = run
      ? `/api/runs/${run.run_id}/artifacts/${encodeURIComponent(selectedFile)}`
      : `${baseUrl}artifacts/${selectedFile}`;
    fetch(url)
      .then((response) => {
        if (!response.ok) throw new Error(`Artifact unavailable: ${selectedFile}`);
        return response.text();
      })
      .then(setFileContent)
      .catch((reason: Error) => setFileContent(reason.message));
  }, [run?.run_id, selectedFile]);

  const startRun = useCallback(async () => {
    if (!config || !canStart) return;
    setError(null);
    setSelectedFile(null);
    const created = await jsonRequest<Run>("/api/runs", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ request: config.default_request }),
    });
    setRun(created);
    void refresh(created.run_id);
  }, [canStart, config, refresh]);

  const approve = useCallback(async () => {
    const owner = proof?.datahub_context.owners[0]?.urn;
    if (!run || !owner || run.state !== "PLANNED") return;
    setError(null);
    await jsonRequest<Run>(`/api/runs/${run.run_id}/approve`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ approved_by: owner }),
    });
    void refresh(run.run_id);
  }, [proof, refresh, run]);

  const activeStage = useMemo(() => {
    if (run?.state === "COMPLETED") return 5;
    if (run?.state === "PLANNED") return 3;
    const last = run?.events.at(-1)?.stage;
    const index = last ? stages.indexOf(last) : 0;
    return index >= 0 ? index : 0;
  }, [run]);

  const stageState = useCallback((index: number) => {
    if (!run && capturedProof) return "complete";
    if (!run) return index === 0 ? "ready" : "locked";
    if (run?.state === "COMPLETED") return "complete";
    if (index < activeStage) return "complete";
    if (index === activeStage) return isBusy ? "active" : isApproval ? "paused" : "active";
    return "locked";
  }, [activeStage, capturedProof, isApproval, isBusy, run]);

  if (!request) {
    return <main className="loading">{error ?? "Connecting to Rehearsal…"}</main>;
  }

  return (
    <main className="app-shell">
      <header className="topbar">
        <div className="brand"><span className="brand-mark">R</span><span>Rehearsal</span></div>
        <div className="change-title">
          <strong>{request.repository_model}.{request.source_column}</strong>
          <span>→</span>
          <strong>{request.target_column}</strong>
        </div>
        <TopFact label="RISK" value={proof ? `${proof.decision.risk} · ${proof.decision.score}/100` : "Calculating"} risk />
        <TopFact label="IMPACT" value={proof ? `${proof.datahub_context.downstream.length} downstream consumers` : "Reading lineage"} />
        <div className="connection"><i className={config?.mode === "live" ? "live" : "captured"} /><span>{connectionLabel}</span></div>
        <button className="run-button" onClick={() => void startRun()} disabled={!canStart}>
          {isBusy ? "Running…" : isApproval ? "Awaiting approval" : run ? "Run again" : isApi ? "Run rehearsal" : "Evidence replay"}
        </button>
      </header>

      {error && <div className="error-banner">{error}</div>}
      {run?.error && <div className="error-banner">{run.error}</div>}

      <section className="primary-grid">
        <ExecutionRail run={run} activeStage={activeStage} stageState={stageState} />

        <section className="workbench">
          <EvidencePanel proof={proof} request={request} />
          <PacketPanel
            proof={proof}
            selectedFile={selectedFile}
            setSelectedFile={setSelectedFile}
            fileContent={fileContent}
          />
        </section>

        <ApprovalInspector
          proof={proof}
          run={run}
          isApi={isApi}
          mode={run?.mode ?? config?.mode ?? "captured"}
          approve={approve}
        />
      </section>

      <ActivityLedger events={run?.events ?? []} proof={capturedProof} isApi={isApi} />
    </main>
  );
}

function TopFact({ label, value, risk = false }: { label: string; value: string; risk?: boolean }) {
  return <div className="top-fact"><span>{label}</span><b className={risk ? "risk" : ""}>{value}</b></div>;
}

function ExecutionRail({
  run,
  activeStage,
  stageState,
}: {
  run: Run | null;
  activeStage: number;
  stageState: (index: number) => string;
}) {
  return (
    <aside className="execution-rail">
      <h2>EXECUTION</h2>
      <ol>
        {stages.map((stage, index) => {
          const state = stageState(index);
          const event = [...(run?.events ?? [])].reverse().find((item) => item.stage === stage);
          return (
            <li key={stage} className={state}>
              <span className="step-number">{index + 1}</span>
              <div>
                <strong>{stage[0] + stage.slice(1).toLowerCase()}</strong>
                <small>{event?.detail ?? (index === 0 ? "Ready to validate" : "Awaiting prior stage")}</small>
                {index === activeStage && <em>{state === "paused" ? "PAUSED" : state === "active" ? "RUNNING" : state === "ready" ? "READY" : "DONE"}</em>}
              </div>
              <i aria-label={state}>{state === "complete" ? "✓" : state === "locked" ? "·" : ""}</i>
            </li>
          );
        })}
      </ol>
    </aside>
  );
}

function EvidencePanel({ proof, request }: { proof: Proof | null; request: Request }) {
  const context = proof?.datahub_context;
  return (
    <section className="evidence-panel">
      <div className="panel-heading"><span>{proof ? "DATAHUB EVIDENCE" : "CONTEXT ACQUISITION"}</span><small>{context?.platform ?? "Waiting for catalog"}</small></div>
      {!context ? (
        <div className="empty-state"><i /><strong>Catalog evidence has not been read</strong><span>Start the rehearsal to resolve schema, ownership, governance, lineage, assertions, and queries.</span></div>
      ) : (
        <>
          <div className="source-row">
            <span className="dataset-icon" />
            <div><strong>{context.asset_name}</strong><small>{request.source_column} → {request.target_column}</small></div>
            <dl>
              <div><dt>Owner</dt><dd>{ownerName(context.owners[0]?.urn)}</dd></div>
              <div><dt>Governance</dt><dd>{context.tags.join(" · ") || "None"}</dd></div>
            </dl>
          </div>
          <div className="lineage-tree">
            {context.downstream.map((consumer) => (
              <div className="consumer-row" key={consumer.urn}>
                <span className="consumer-icon" />
                <div><strong>{consumer.name}</strong><small>{consumer.entity_type} · {consumer.platform ?? "unknown"}</small></div>
                <code>{consumer.impacted_columns.join(", ") || "asset-level evidence"}</code>
              </div>
            ))}
          </div>
          <div className="evidence-foot">
            <span>Column lineage: {context.downstream.filter((item) => item.impacted_columns.length > 0).length}/{context.downstream.length}</span>
            <span>Known queries: {context.query_examples.length}</span>
          </div>
        </>
      )}
    </section>
  );
}

function PacketPanel({
  proof,
  selectedFile,
  setSelectedFile,
  fileContent,
}: {
  proof: Proof | null;
  selectedFile: string | null;
  setSelectedFile: (value: string) => void;
  fileContent: string;
}) {
  return (
    <section className="packet-panel">
      <div className="panel-heading"><span>MIGRATION PACKET</span><small>{proof ? `${proof.artifacts.length} review artifacts` : "Not compiled"}</small></div>
      {proof ? (
        <>
          <div className="file-tabs" aria-label="Migration packet files">
            {proof.artifacts.map((artifact) => (
              <button
                key={artifact.relative_path}
                className={artifact.relative_path === selectedFile ? "selected" : ""}
                onClick={() => setSelectedFile(artifact.relative_path)}
              >
                <span>▱</span>{artifact.relative_path}
              </button>
            ))}
          </div>
          <div className="code-heading"><span>{selectedFile ?? "Artifact"}</span><small>{proof.status === "PLANNED" ? "FROZEN · NO WRITES EXECUTED" : proof.status}</small></div>
          <pre aria-label={`${selectedFile ?? "artifact"} source`}><code>{fileContent}</code></pre>
        </>
      ) : (
        <div className="empty-state compact"><i /><strong>No packet exists yet</strong><span>Compilation starts only after complete DataHub context passes policy.</span></div>
      )}
    </section>
  );
}

function ApprovalInspector({
  proof,
  run,
  isApi,
  mode,
  approve,
}: {
  proof: Proof | null;
  run: Run | null;
  isApi: boolean;
  mode: "fixture" | "live" | "captured";
  approve: () => Promise<void>;
}) {
  const waiting = run?.state === "PLANNED";
  const completed = proof?.status === "COMPLETED";
  const verification = proof?.verification;
  const queryCount = Number(verification?.actual_datahub_queries_replayed ?? 0);
  const sourceRows = Number(verification?.source_row_count_after_rollback ?? 0);
  const contractExecuted = Boolean(verification?.contract_executed);
  const writebackCheck = mode === "live"
    ? [`Fresh DataHub read · ${String(Boolean(proof?.datahub_writeback.fresh_read_verified))}`, Boolean(proof?.datahub_writeback.fresh_read_verified)] as const
    : [
      "Catalog write-back · none",
      proof?.datahub_writeback.operations.length === 0
        && proof?.datahub_writeback.fresh_read_verified === false,
    ] as const;
  const checks = completed && verification
    ? [
      [`Catalog queries replayed · ${queryCount}`, queryCount > 0],
      [`New source write visible · ${String(Boolean(verification.new_write_visible))}`, Boolean(verification.new_write_visible)],
      [`Rollback restored · ${sourceRows} source rows retained`, Boolean(verification.rollback_restored) && sourceRows > 0],
      [`Contract executed · ${String(contractExecuted)}`, !contractExecuted],
      writebackCheck,
    ] as const
    : [
      ["Context frozen", Boolean(proof?.context_digest)],
      ["Owner present in DataHub", Boolean(proof?.datahub_context.owners.length)],
      ["Additive expand only", Boolean(proof?.artifacts.length)],
      ["No write-back before approval", !proof?.datahub_writeback.operations.length],
    ] as const;

  return (
    <aside className="approval-inspector">
      <h2>APPROVAL INSPECTOR</h2>
      <section className={waiting ? "approval-callout waiting" : completed ? "approval-callout completed" : "approval-callout"}>
        <strong>{waiting ? "Owner approval required" : completed ? "Packet verified" : "Awaiting compiled packet"}</strong>
        <span>{waiting ? "Verification and DataHub write-back are locked." : completed ? "The compatibility view and rollback ran inside the isolated rehearsal." : "No approval can be recorded before context is frozen."}</span>
        <button disabled={!waiting || !isApi} onClick={() => void approve()}>{waiting ? "Approve frozen packet" : completed ? "Approval recorded" : "Approval locked"}</button>
      </section>

      <section className="inspector-section">
        <div className="inspector-title"><span>RISK REASONS</span>{proof && <b>{proof.decision.risk} · {proof.decision.score}/100</b>}</div>
        <ul>{proof?.decision.reasons.map((reason) => <li key={reason}>{reason}</li>) ?? <li>Waiting for deterministic policy</li>}</ul>
      </section>

      <section className="inspector-section digest">
        <div className="inspector-title"><span>FROZEN EVIDENCE</span></div>
        <dl>
          <div><dt>Context</dt><dd>{shortDigest(proof?.context_digest)}</dd></div>
          <div><dt>Packet</dt><dd>{shortDigest(proof?.packet_digest)}</dd></div>
          <div><dt>Owner</dt><dd>{ownerName(proof?.datahub_context.owners[0]?.urn)}</dd></div>
          <div><dt>Mode</dt><dd>{mode}</dd></div>
        </dl>
      </section>

      <section className="inspector-section guardrails">
        <div className="inspector-title"><span>{completed ? "EXECUTED CHECKS" : "PRE-APPROVAL GUARDRAILS"}</span></div>
        {checks.map(([label, passed]) => <div key={label}><i className={passed ? "pass" : "pending"}>{passed ? "✓" : "·"}</i><span>{label}</span></div>)}
      </section>
    </aside>
  );
}

function ActivityLedger({ events, proof, isApi }: { events: RunEvent[]; proof: Proof | null; isApi: boolean }) {
  const rows = events.length > 0
    ? [...events].reverse()
    : proof
      ? [{ sequence: 1, at: proof.approval?.approved_at ?? "", stage: "PROOF", operation: "captured_fixture_run", detail: "Captured fixture proof; use the local client for real-time operations", data: {} }]
      : [];
  return (
    <section className="activity-ledger">
      <div className="ledger-heading"><span>ACTIVITY LEDGER</span><small>{events.length > 0 ? "actual runtime boundaries" : isApi ? "waiting for runtime" : "captured evidence mode"}</small></div>
      <div className="ledger-table">
        <div className="ledger-row head"><span>TIME (UTC)</span><span>STAGE</span><span>OPERATION</span><span>DETAIL</span></div>
        {rows.slice(0, 7).map((event) => (
          <div className="ledger-row" key={`${event.sequence}-${event.operation}`}>
            <time>{event.at ? timeLabel(event.at) : "captured"}</time>
            <b>{event.stage}</b>
            <span>{humanOperation(event.operation)}</span>
            <span>{event.detail}</span>
          </div>
        ))}
        {rows.length === 0 && <div className="ledger-empty">No operation has executed. Start the rehearsal to populate this ledger.</div>}
      </div>
    </section>
  );
}
